import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppTab } from './appLayoutConfig';
import { ChartWrapper, TableComponent } from './dsl-components';
import { Spinner } from 'react-bootstrap';
import api from '../../../api';
import './system-manager-wrapper.scss';
import BugKanbanBoard from './BugKanbanBoard';
import RoadmapBoard from './RoadmapBoard';
import {
  GetUnapprovedCompilationsResponse,
  UnapprovedProactivityCompilation,
} from '@dreamai/types';

interface CustomerActivityReportResponse {
  success: boolean;
  chartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      backgroundColor: string;
      fill: boolean;
      tension: number;
    }[];
  };
  chartOptions: Record<string, unknown>;
  summaryTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
  dauTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
  activeUsersTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
}

interface ActiveUser {
  userId: string;
  userName: string;
  companyId: number;
  companyName: string;
  messageCount: number;
  threadCount: number;
}

interface ActiveUsersResponse {
  success: boolean;
  users: ActiveUser[];
  totalCount: number;
}

interface UserThread {
  threadId: string;
  agentId: string;
  threadDescription: string | null;
  agentNickname: string | null;
  agentType: string;
  updatedAt: string;
  happiness: 'red' | 'amber' | 'green';
  happinessContext: string | null;
  threadSummary: string | null;
  last48hSummary: string | null;
  summaryGeneratedAt: string | null;
}

interface UserThreadsResponse {
  success: boolean;
  threads: UserThread[];
  totalCount: number;
}

interface SystemManagerWrapperProps {
  threadId?: string;
  tabId: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

const SYSTEM_MANAGER_TABS: AppTab[] = [
  { id: 'activity', label: 'Customer Activity' },
  { id: 'threads', label: 'Active Threads' },
  { id: 'bugs', label: 'Bug Tracker' },
  { id: 'roadmap', label: 'Product Roadmap' },
  { id: 'proactivity', label: 'Proactivity Review' },
];

const SystemManagerWrapper: React.FC<SystemManagerWrapperProps> = ({
  tabId,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activityData, setActivityData] =
    useState<CustomerActivityReportResponse | null>(null);
  const [activeUsersData, setActiveUsersData] =
    useState<ActiveUsersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Accordion state - which user is expanded and their threads
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userThreads, setUserThreads] = useState<Map<string, UserThread[]>>(
    new Map(),
  );
  const [loadingThreadsFor, setLoadingThreadsFor] = useState<string | null>(
    null,
  );

  // Proactivity review state
  const [proactivityData, setProactivityData] = useState<
    UnapprovedProactivityCompilation[]
  >([]);
  const [proactivityLoading, setProactivityLoading] = useState(false);
  const [proactivityError, setProactivityError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Notify parent about available tabs synchronously
  useLayoutEffect(() => {
    if (onTabsReady) {
      onTabsReady(SYSTEM_MANAGER_TABS);
    }
  }, [onTabsReady]);

  // Fetch activity data when on activity tab
  useEffect(() => {
    if (tabId !== 'activity') return;

    const fetchActivityData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<CustomerActivityReportResponse>(
          '/customer-activity-report',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setActivityData(response.data);
        } else {
          setError('Failed to load activity data');
        }
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load activity data',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [tabId, getAccessTokenSilently]);

  // Fetch active users when on threads tab
  useEffect(() => {
    if (tabId !== 'threads') return;

    const fetchActiveUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<ActiveUsersResponse>(
          '/customer-activity-report/active-users',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setActiveUsersData(response.data);
        } else {
          setUsersError('Failed to load active users');
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        setUsersError(
          err instanceof Error ? err.message : 'Failed to load active users',
        );
      } finally {
        setUsersLoading(false);
      }
    };

    fetchActiveUsers();
  }, [tabId, getAccessTokenSilently]);

  // Fetch threads for a user when accordion is expanded
  const fetchUserThreads = useCallback(
    async (userId: string) => {
      if (userThreads.has(userId)) return; // Already loaded

      setLoadingThreadsFor(userId);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<UserThreadsResponse>(
          `/customer-activity-report/user-threads/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setUserThreads((prev) =>
            new Map(prev).set(userId, response.data.threads),
          );
        }
      } catch (err) {
        console.error('Error fetching user threads:', err);
      } finally {
        setLoadingThreadsFor(null);
      }
    },
    [getAccessTokenSilently, userThreads],
  );

  // Handle accordion toggle
  const handleUserClick = useCallback(
    (userId: string) => {
      if (expandedUserId === userId) {
        setExpandedUserId(null);
      } else {
        setExpandedUserId(userId);
        fetchUserThreads(userId);
      }
    },
    [expandedUserId, fetchUserThreads],
  );

  // Fetch proactivity data when on proactivity tab
  useEffect(() => {
    if (tabId !== 'proactivity') return;

    const fetchProactivityData = async () => {
      setProactivityLoading(true);
      setProactivityError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetUnapprovedCompilationsResponse>(
          '/proactivity/compilations/unapproved',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setProactivityData(response.data.compilations);
        } else {
          setProactivityError('Failed to load proactivity data');
        }
      } catch (err) {
        console.error('Error fetching proactivity data:', err);
        setProactivityError(
          err instanceof Error
            ? err.message
            : 'Failed to load proactivity data',
        );
      } finally {
        setProactivityLoading(false);
      }
    };

    fetchProactivityData();
  }, [tabId, getAccessTokenSilently]);

  // Approve a proactivity compilation
  const handleApproveCompilation = useCallback(
    async (compilationId: string) => {
      setApprovingId(compilationId);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post(
          `/proactivity/compilations/${compilationId}/approve`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          // Remove from list
          setProactivityData((prev) =>
            prev.filter((c) => c.id !== compilationId),
          );
        }
      } catch (err) {
        console.error('Error approving compilation:', err);
      } finally {
        setApprovingId(null);
      }
    },
    [getAccessTokenSilently],
  );

  // Render the appropriate component based on selected tab
  const renderTabContent = () => {
    switch (tabId) {
      case 'activity':
        if (loading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading customer activity data...</p>
            </div>
          );
        }

        if (error) {
          return (
            <div className='activity-error'>
              <p>{error}</p>
            </div>
          );
        }

        if (!activityData) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading...</p>
            </div>
          );
        }

        return (
          <div className='activity-content'>
            <h2>Customer Activity Report</h2>

            <div className='chart-section'>
              <ChartWrapper
                type='line'
                data={activityData.chartData}
                options={activityData.chartOptions}
              />
            </div>

            <div className='tables-section'>
              <div className='table-group'>
                <h3>Summary</h3>
                <TableComponent
                  headingArray={activityData.summaryTable.headingArray}
                  valuesArrays={activityData.summaryTable.valuesArrays}
                />
              </div>

              <div className='table-group'>
                <h3>Daily Active Users (Last 7 Days)</h3>
                <TableComponent
                  headingArray={activityData.dauTable.headingArray}
                  valuesArrays={activityData.dauTable.valuesArrays}
                />
              </div>
            </div>

            <div className='active-users-section'>
              <h3>Active Users Yesterday (Top 10)</h3>
              <TableComponent
                headingArray={activityData.activeUsersTable.headingArray}
                valuesArrays={activityData.activeUsersTable.valuesArrays}
              />
            </div>
          </div>
        );
      case 'threads':
        if (usersLoading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading active users...</p>
            </div>
          );
        }

        if (usersError) {
          return (
            <div className='activity-error'>
              <p>{usersError}</p>
            </div>
          );
        }

        if (!activeUsersData) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading...</p>
            </div>
          );
        }

        return (
          <div className='active-threads-content'>
            <h2>Active Users (Last 48 Hours)</h2>
            <p className='threads-subtitle'>
              {activeUsersData.totalCount} users with recent activity
            </p>

            <div className='users-accordion'>
              {activeUsersData.users.map((user) => {
                const isExpanded = expandedUserId === user.userId;
                const threads = userThreads.get(user.userId);
                const isLoadingThreads = loadingThreadsFor === user.userId;

                return (
                  <div key={user.userId} className='user-accordion-item'>
                    <button
                      className={`user-accordion-header ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleUserClick(user.userId)}
                    >
                      <div className='user-info'>
                        <span className='user-name'>{user.userName}</span>
                        <span className='company-name'>{user.companyName}</span>
                      </div>
                      <div className='user-stats'>
                        <span className='stat'>
                          {user.messageCount} messages
                        </span>
                        <span className='stat'>{user.threadCount} threads</span>
                      </div>
                      <span className='expand-icon'>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className='user-threads-panel'>
                        {isLoadingThreads && (
                          <div className='threads-loading'>
                            <Spinner animation='border' size='sm' />
                            <span>Analyzing threads...</span>
                          </div>
                        )}

                        {!isLoadingThreads && threads && threads.length > 0 && (
                          <div className='threads-list'>
                            {threads.map((thread) => (
                              <div
                                key={thread.threadId}
                                className={`thread-card happiness-${thread.happiness}`}
                              >
                                <div className='thread-header'>
                                  <div
                                    className={`happiness-indicator ${thread.happiness}`}
                                    title={
                                      thread.happinessContext ||
                                      'No context available'
                                    }
                                  >
                                    {thread.happiness === 'red' && '🔴'}
                                    {thread.happiness === 'amber' && '🟡'}
                                    {thread.happiness === 'green' && '🟢'}
                                  </div>
                                  <div className='thread-title'>
                                    {thread.threadDescription ||
                                      'Untitled thread'}
                                  </div>
                                  <div className='agent-info'>
                                    {thread.agentNickname
                                      ? `${thread.agentNickname} - Your Business`
                                      : thread.agentType}
                                  </div>
                                </div>

                                {thread.last48hSummary && (
                                  <div className='recent-activity'>
                                    <strong>Recent:</strong>{' '}
                                    {thread.last48hSummary}
                                  </div>
                                )}

                                {thread.happinessContext && (
                                  <div className='happiness-context'>
                                    <strong>Assessment:</strong>{' '}
                                    {thread.happinessContext}
                                  </div>
                                )}

                                <div className='thread-footer'>
                                  <span className='updated-at'>
                                    {new Date(thread.updatedAt).toLocaleString(
                                      'en-GB',
                                      {
                                        day: 'numeric',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      },
                                    )}
                                  </span>
                                  <a
                                    href={`/conversations?agent_id=${thread.agentId}&thread_id=${thread.threadId}&app_tab=threads`}
                                    className='view-thread-btn'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    View →
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {!isLoadingThreads &&
                          threads &&
                          threads.length === 0 && (
                            <div className='no-threads'>
                              <p>No active threads for this user</p>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}

              {activeUsersData.users.length === 0 && (
                <div className='no-threads'>
                  <p>No active users in the last 48 hours</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'bugs':
        return <BugKanbanBoard />;
      case 'roadmap':
        return <RoadmapBoard />;
      case 'proactivity':
        if (proactivityLoading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading proactivity compilations...</p>
            </div>
          );
        }

        if (proactivityError) {
          return (
            <div className='activity-error'>
              <p>{proactivityError}</p>
            </div>
          );
        }

        return (
          <div className='proactivity-content'>
            <h2>Proactivity Review</h2>
            <p className='proactivity-subtitle'>
              {proactivityData.length} compilation
              {proactivityData.length !== 1 ? 's' : ''} awaiting approval
            </p>

            {proactivityData.length === 0 ? (
              <div className='no-compilations'>
                <p>No compilations pending approval</p>
              </div>
            ) : (
              <div className='compilations-list'>
                {proactivityData.map((compilation) => (
                  <div key={compilation.id} className='compilation-card'>
                    <div className='compilation-header'>
                      <div className='compilation-title'>
                        {compilation.title}
                      </div>
                      <div className='compilation-meta'>
                        <span className='user-name'>
                          {compilation.userName || 'Unknown User'}
                        </span>
                        <span className='company-name'>
                          {compilation.companyName || 'Unknown Company'}
                        </span>
                      </div>
                    </div>

                    <div className='compilation-body'>
                      <div className='compilation-subtitle'>
                        {compilation.subtitle}
                      </div>
                      {compilation.synthesizedPostChats && (
                        <div className='compilation-summary'>
                          {compilation.synthesizedPostChats}
                        </div>
                      )}
                      <div className='compilation-stats'>
                        <span>{compilation.itemsCount || 0} items</span>
                        {compilation.compiledAt && (
                          <span>
                            {new Date(compilation.compiledAt).toLocaleString(
                              'en-GB',
                              {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className='compilation-actions'>
                      <button
                        className='approve-btn'
                        onClick={() => handleApproveCompilation(compilation.id)}
                        disabled={approvingId === compilation.id}
                      >
                        {approvingId === compilation.id
                          ? 'Approving...'
                          : 'Approve'}
                      </button>
                      {compilation.threadId && (
                        <a
                          href={`/conversations?thread_id=${compilation.threadId}`}
                          className='view-thread-btn'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          View Thread →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return <div className='system-manager-wrapper'>{renderTabContent()}</div>;
};

export default SystemManagerWrapper;
